import { t } from '@lingui/core/macro'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  normalizeForMatching,
  useTimeSeriesLinkContext,
} from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import { useCantileverHostsForDocumentTab } from 'features/knowledgeDocumentViewer/hooks/useCantileverHostsForDocumentTab'
import { useDocumentSubjects } from 'features/knowledgeDocumentViewer/hooks/useDocumentSubjects'
import type {
  AliasHighlight,
  DocumentSubject,
} from 'features/knowledgeDocumentViewer/types/documentSubjects'
import {
  BoundingBoxJsonItem,
  DocumentOcrPage,
  normalizeOcrPages,
} from 'features/knowledgeDocumentViewer/types/ocrViewer'
import {
  type DocumentOcrEntry,
  getIntraDocSearchDocumentId,
  getSystemDocumentBqId,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { flattenAliasHighlights } from 'features/knowledgeDocumentViewer/utils/aliasHighlights'
import { mergeTagLinkedFirst } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import { resolveUuid5PageImageLookupUnavailable } from 'features/knowledgeDocumentViewer/utils/pageImageRolodexSettle'
import { useGeckoNotifications } from 'features/notifications'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import type { MatchedDatapoint } from 'features/timeSeries'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  KnowledgeDocumentEntry,
  RolodexEntryType,
  RolodexSignedURLResult,
} from 'types'

interface DocumentTabDataContextType {
  boundingBoxDataState: [DocumentOcrPage, DocumentOcrPage]
  setBoundingBoxDataState: (data: [DocumentOcrPage, DocumentOcrPage]) => void
  documentFilename: string | undefined
  documentSignedURL: RolodexSignedURLResult | undefined
  originalDocumentSignedURL: RolodexSignedURLResult | undefined
  intraDocSearchDocumentId: string
  sourceDocumentIdPresent: boolean
  documentTableRow: DocumentTableRow | undefined
  documentType: string | undefined
  saveToRolodex: (
    newState: DocumentOcrPage,
    pageIndex?: number
  ) => Promise<void>
  updateBoxLabel: (boxId: string, newLabel: string) => Promise<void>
  knowledgeDocumentEntriesForSelectedDocument: KnowledgeDocumentEntry[]
  isLoadingKnowledgeDocuments: boolean
  /** Pipeline-config lookup resolved with no match — broken deep link or out-of-scope document. */
  isSelectedDocumentUnavailable: boolean
  selectedDocumentOcrEntries: DocumentOcrEntry[] | undefined
  systemDocumentEntryId: string | undefined
  /** Cantilever-document entry id that hosts the PDF when tagged. */
  cantileverDocumentEntryId: string | undefined
  totalPages: number
  pageNumber: number
  documentSubjects: DocumentSubject[]
  panelSubjectUid: string | undefined
  aliasHighlights: AliasHighlight[]
  docReferenceSubjects: DocumentSubject[]
  docReferenceHighlights: AliasHighlight[]
  /** Label-keyed time-series lookup: tag-linked datapoints first, then text match. */
  hasTimeSeriesData: (instrumentText: string | undefined) => boolean
  getMatchedDatapoints: (
    instrumentText: string | undefined
  ) => MatchedDatapoint[]
  siteID: string
  documentId: string
  bqDocumentId: string | undefined
  imageDocumentId: string | undefined
  /** System-document lookup finished without a usable page-image identity. */
  isUuid5PageImageLookupUnavailable: boolean
}

interface DocumentTabDataProviderProps {
  children: React.ReactNode
  documentId: string
  pageNumber: number
  siteID: string
  panelSubjectUid?: string
}

export const DocumentTabDataProvider: React.FC<
  DocumentTabDataProviderProps
> = ({ children, documentId, pageNumber, siteID, panelSubjectUid }) => {
  const { isEvalSessionLocked } = useKnowledgeDocumentUXContext()
  const { rolodexPatch } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const [boundingBoxDataState, setBoundingBoxDataState] = useState<
    [DocumentOcrPage, DocumentOcrPage]
  >([
    { page_number: 1, confirmed: [], unconfirmed: [] },
    { page_number: 2, confirmed: [], unconfirmed: [] },
  ])

  const {
    systemDocumentEntry,
    systemDocumentEntryId,
    cantileverDocumentEntryId,
    documentFilename,
    documentSignedURL,
    originalDocumentSignedURL,
    totalPages,
    isSelectedDocumentUnavailable,
    isLoadingSelectedDocument,
  } = useCantileverHostsForDocumentTab(documentId, siteID)

  const selectedDocumentOcrLoadOptions = useMemo(() => {
    if (
      isEvalSessionLocked ||
      isPipelineConfigScopeLoading ||
      !systemDocumentEntryId
    ) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({
      site: siteID,
      'system-document': systemDocumentEntryId,
    })
    if (!scopedTags) return undefined

    return {
      type: [RolodexEntryType.DOCUMENT_OCR],
      tags: scopedTags,
      limit: 1,
    }
  }, [
    getPipelineConfigScopedTags,
    isEvalSessionLocked,
    isPipelineConfigScopeLoading,
    siteID,
    systemDocumentEntryId,
  ])
  const { data: selectedDocumentOcrEntries } =
    useGetRolodexEntriesByLoadOptions<DocumentOcrEntry[]>(
      selectedDocumentOcrLoadOptions
    )

  const knowledgeDocumentLoadOptions = useMemo(() => {
    if (
      isEvalSessionLocked ||
      isPipelineConfigScopeLoading ||
      !systemDocumentEntryId
    ) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({
      site: siteID,
      'system-document': systemDocumentEntryId,
    })
    if (!scopedTags) return undefined

    return {
      type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
      tags: scopedTags,
      limit: 1000000,
    }
  }, [
    getPipelineConfigScopedTags,
    isEvalSessionLocked,
    isPipelineConfigScopeLoading,
    siteID,
    systemDocumentEntryId,
  ])
  const {
    data: knowledgeDocumentEntriesForSelectedDocument,
    isLoading: isLoadingKnowledgeDocuments,
  } = useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>(
    knowledgeDocumentLoadOptions
  )

  const sourceDocumentIdPresent = Boolean(
    systemDocumentEntry?.entry.data.source_document_id
  )
  const intraDocSearchDocumentId = systemDocumentEntry
    ? getIntraDocSearchDocumentId(systemDocumentEntry)
    : documentId
  const bqDocumentId = systemDocumentEntry
    ? getSystemDocumentBqId(systemDocumentEntry)
    : undefined
  const imageDocumentId = isEvalSessionLocked ? documentId : bqDocumentId
  const isUuid5PageImageLookupUnavailable =
    resolveUuid5PageImageLookupUnavailable({
      isSystemDocumentLoading: isLoadingSelectedDocument,
      bqDocumentId: imageDocumentId,
    })
  const { data: documentSubjectsData } = useDocumentSubjects(
    siteID,
    isEvalSessionLocked ? undefined : bqDocumentId
  )
  const allSubjects = useMemo(
    () => documentSubjectsData?.subjects ?? [],
    [documentSubjectsData]
  )
  const documentSubjects = useMemo(
    () => allSubjects.filter((s) => s.kind !== 'doc_reference'),
    [allSubjects]
  )
  const docReferenceSubjects = useMemo(
    () => allSubjects.filter((s) => s.kind === 'doc_reference'),
    [allSubjects]
  )

  const aliasHighlights = useMemo<AliasHighlight[]>(
    () => flattenAliasHighlights(documentSubjects),
    [documentSubjects]
  )

  const docReferenceHighlights = useMemo<AliasHighlight[]>(
    () => flattenAliasHighlights(docReferenceSubjects),
    [docReferenceSubjects]
  )

  const {
    hasTimeSeriesData: textHasTimeSeriesData,
    getMatchedDatapoints: textGetMatchedDatapoints,
    getDatapointsByIds,
  } = useTimeSeriesLinkContext()

  const tagLinkedByLabel = useMemo(() => {
    const byLabel = new Map<string, MatchedDatapoint[]>()
    for (const subject of documentSubjects) {
      const tagLinked = getDatapointsByIds(subject.datapoints ?? [])
      if (tagLinked.length === 0) continue
      for (const text of [
        subject.name,
        ...subject.aliases.map((alias) => alias.value),
      ]) {
        if (!text) continue
        const key = normalizeForMatching(text)
        if (!byLabel.has(key)) byLabel.set(key, tagLinked)
      }
    }
    return byLabel
  }, [documentSubjects, getDatapointsByIds])

  const tagLinkedFor = useCallback(
    (label: string | undefined): MatchedDatapoint[] =>
      label ? (tagLinkedByLabel.get(normalizeForMatching(label)) ?? []) : [],
    [tagLinkedByLabel]
  )

  const hasTimeSeriesData = useCallback(
    (label: string | undefined): boolean =>
      tagLinkedFor(label).length > 0 || textHasTimeSeriesData(label),
    [tagLinkedFor, textHasTimeSeriesData]
  )

  const getMatchedDatapoints = useCallback(
    (label: string | undefined): MatchedDatapoint[] =>
      mergeTagLinkedFirst(tagLinkedFor(label), textGetMatchedDatapoints(label)),
    [tagLinkedFor, textGetMatchedDatapoints]
  )

  useEffect(() => {
    if (documentId) {
      const pages = normalizeOcrPages(
        selectedDocumentOcrEntries?.[0]?.entry.data?.pages ?? []
      )
      const leftPage = pages.find((p) => p.page_number === pageNumber) ?? {
        page_number: 1,
        confirmed: [],
        unconfirmed: [],
      }
      const rightPage = pages.find((p) => p.page_number === pageNumber + 1) ?? {
        page_number: 2,
        confirmed: [],
        unconfirmed: [],
      }
      setBoundingBoxDataState([leftPage, rightPage])
    }
  }, [documentId, pageNumber, selectedDocumentOcrEntries])

  const saveToRolodex = async (
    newState: DocumentOcrPage,
    pageIndex: number = 0
  ) => {
    const selectedOcrEntry = selectedDocumentOcrEntries?.[0]

    if (!selectedOcrEntry) {
      return
    }

    const newPages = normalizeOcrPages(
      selectedOcrEntry.entry.data?.pages ?? []
    ).map((page) => {
      if (page.page_number === pageNumber + pageIndex) {
        return newState
      }
      return page
    })
    try {
      await rolodexPatch([
        {
          id: selectedOcrEntry.entry.id,
          data: {
            ...selectedOcrEntry.entry.data,
            pages: newPages,
          },
        },
      ])
      successNotification(t`Saved to Rolodex`)
    } catch (error) {
      console.error('Error saving to Rolodex:', error)
      errorNotification(t`Failed to save to Rolodex`)
      throw error
    }
  }

  const updateBoxLabel = async (boxId: string, newLabel: string) => {
    const lastDash = boxId.lastIndexOf('-')
    const boxPageIndex = lastDash !== -1 ? Number(boxId.slice(lastDash + 1)) : 0
    const rawId = lastDash !== -1 ? boxId.slice(0, lastDash) : boxId
    const currentPageData = boundingBoxDataState[boxPageIndex]

    const updateBoxInList = (boxes: BoundingBoxJsonItem[]) =>
      boxes.map((b) => (String(b.id) === rawId ? { ...b, text: newLabel } : b))

    const updatedConfirmed = updateBoxInList(currentPageData.confirmed)
    const updatedUnconfirmed = updateBoxInList(currentPageData.unconfirmed)

    const updatedPageData = {
      ...currentPageData,
      confirmed: updatedConfirmed,
      unconfirmed: updatedUnconfirmed,
    }

    const newState: [DocumentOcrPage, DocumentOcrPage] = [
      boxPageIndex === 0 ? updatedPageData : boundingBoxDataState[0],
      boxPageIndex === 1 ? updatedPageData : boundingBoxDataState[1],
    ]
    setBoundingBoxDataState(newState)
    await saveToRolodex(updatedPageData, boxPageIndex)
  }

  const documentType = systemDocumentEntry?.entry.data?.type

  const documentTableRow = useMemo(() => {
    return systemDocumentEntry
      ? transformSystemDocumentToTableRow(systemDocumentEntry)
      : undefined
  }, [systemDocumentEntry])

  const contextValue: DocumentTabDataContextType = {
    boundingBoxDataState,
    setBoundingBoxDataState,
    documentFilename,
    documentSignedURL,
    originalDocumentSignedURL,
    intraDocSearchDocumentId,
    sourceDocumentIdPresent,
    documentTableRow,
    documentType,
    saveToRolodex,
    updateBoxLabel,
    knowledgeDocumentEntriesForSelectedDocument:
      knowledgeDocumentEntriesForSelectedDocument ?? [],
    isLoadingKnowledgeDocuments,
    isSelectedDocumentUnavailable,
    selectedDocumentOcrEntries,
    systemDocumentEntryId,
    cantileverDocumentEntryId,
    totalPages,
    pageNumber,
    documentSubjects,
    panelSubjectUid,
    aliasHighlights,
    docReferenceSubjects,
    docReferenceHighlights,
    hasTimeSeriesData,
    getMatchedDatapoints,
    siteID,
    documentId,
    bqDocumentId,
    imageDocumentId,
    isUuid5PageImageLookupUnavailable,
  }

  return (
    <DocumentTabDataContext.Provider value={contextValue}>
      {children}
    </DocumentTabDataContext.Provider>
  )
}

const DocumentTabDataContext = createContext<
  DocumentTabDataContextType | undefined
>(undefined)

export const useDocumentTabDataContext = (): DocumentTabDataContextType => {
  const context = useContext(DocumentTabDataContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentTabDataContext must be used within a DocumentTabDataProvider'
    )
  }
  return context
}

/**
 * Document tab data context when inside DocumentTabDataProvider; null otherwise.
 */
export const useDocumentTabDataContextOptional =
  (): DocumentTabDataContextType | null => {
    return useContext(DocumentTabDataContext) ?? null
  }
